// @ts-ignore

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { Button } from "@/components/wpl/button/src/button";
import { Picture } from "@/components/wpl/image/src/image";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";
import { getDragAndDropSectionImage } from "@/templates/software/jira/features/jira-software-features-page/src/localized-images";

const sectionWrapperStyle = css({
    justifyContent: "center",
    textAlign: "center",
    paddingBlockStart: "16px",
    [media.below.sm]: {
        paddingBlockEnd: "32px"
    },
    [media.above.sm]: {
        paddingBlockEnd: "64px"
    }
});

const outerContainerStyle = css({
    padding: "16px",
    display: "flex",
    justifyContent: "center",
    textAlign: "center"
});

const dragAndDropSectionStyle = css({
    "display": "flex",
    "justifyContent": "center",
    "height": "100%",
    "minHeight": "100%",

    [media.below.sm]: {
        backgroundImage: `linear-gradient(180deg, #DEEBFF 0%, #B3D4FF 100%)`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroudnSize: "contain",
        padding: "16px 0",
        width: "100%"
    },
    [media.only.sm]: {
        backgroundImage:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "url(https://wac-cdn.atlassian.com/dam/jcr:606ac063-0873-48b2-bd49-31038e69a4cf/automation-left-720.svg?cdnVersion=1404) , url(https://wac-cdn.atlassian.com/dam/jcr:7b5adf46-b411-47d1-acc0-445b44823b88/automation-right-720.svg?cdnVersion=1404) , linear-gradient(180deg, #DEEBFF 0%, #B3D4FF 100%)",
        backgroundPosition: "left -25px center, right -25px center, center",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundSize: "contain, contain, contain",
        padding: "64px 0",
        width: "83.33333%"
    },
    [media.only.md]: {
        backgroundImage:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "url(https://wac-cdn.atlassian.com/dam/jcr:60f3cf12-216e-431b-bc2d-c6036ac6ca8b/automation-right.svg?cdnVersion=1404) , url(https://wac-cdn.atlassian.com/dam/jcr:90da2812-7e23-4ef8-92af-6ecc802d574b/automation-left.svg?cdnVersion=1404) , linear-gradient(180deg, #DEEBFF 0%, #B3D4FF 100%)",
        backgroundPosition: "right -50px center, left -45px center, center",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundSize: "contain, contain, contain",
        padding: "64px 0",
        width: "83.33333%"
    },
    [media.above.lg]: {
        backgroundImage:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "url(https://wac-cdn.atlassian.com/dam/jcr:60f3cf12-216e-431b-bc2d-c6036ac6ca8b/automation-right.svg?cdnVersion=1404) , url(https://wac-cdn.atlassian.com/dam/jcr:90da2812-7e23-4ef8-92af-6ecc802d574b/automation-left.svg?cdnVersion=1404) , linear-gradient(180deg, #DEEBFF 0%, #B3D4FF 100%)",
        backgroundPosition: "right center, left center, center",
        backgroundRepeat: "no-repeat , no-repeat , no-repeat",
        backgroundSize: "contain, contain, contain",
        padding: "64px 0",
        width: "83.33333%"
    },

    "borderRadius": "10px",
    "data-container-id": "ccfe9692-2616-4962-8245-6ee958f38a8e"
});

const headers = css({
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "1rem"
});

const containerStyles = css({
    [media.below.sm]: {
        width: "100%"
    },
    [media.only.sm]: {
        width: "50%"
    },
    [media.above.md]: {
        width: "66.66666%"
    }
});

const h2Styles = css({
    "display": "flex",
    "textAlign": "center",
    "color": "#253858",
    "letterSpacing": "0",
    "fontWeight": "500",
    "verticalAlign": "middle",

    "@media (min-width: 768px)": {
        fontSize: "2.25rem",
        lineHeight: "1.33333333"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": { verticalAlign: "middle" },

    "marginBlockStart": "0 !important",
    [media.below.md]: {
        marginBlockEnd: "14px !important"
    },
    [media.above.md]: {
        marginBlockEnd: "24px !important"
    }
});

const pStyle = css({
    fontSize: "1rem",
    lineHeight: "1.5",
    textAlign: "center",
    alignContent: "center",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "rgb(9, 30, 66)",
    marginBlockEnd: "24px",
    marginBlockStart: "0"
});

const imageStyles = css({
    display: "flex",
    textAlign: "center",
    justifyContent: "center",
    width: "100%",

    [media.above.sm]: {
        marginBlockStart: "24px"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "400px",
        lineHeight: "16px"
    }
});

const buttonStyles = css({
    display: "inline-flex",
    fontWeight: "500",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #0052cc",
    transition: "all 250ms",
    marginBlockEnd: "24px",
    padding: "4px 10px"
});

const DragAndDropImage = () => {
    const intl = useIntl();
    const localizedImageSrc = getDragAndDropSectionImage(intl.locale);
    return (
        <div css={imageStyles}>
            <Picture
                image={{
                    src: localizedImageSrc,
                    alt: ""
                }}
            />
        </div>
    );
};

const DragAndDropSection = () => {
    const intl = useIntl();

    return (
        <div css={sectionWrapperStyle}>
            <div css={outerContainerStyle}>
                <div css={dragAndDropSectionStyle}>
                    <div css={containerStyles}>
                        <div css={headers}>
                            <h2 css={h2Styles}>
                                {intl.formatMessage({
                                    id: "JiraSoftwareFeaturesPageTemplate.drag-and-drop.title",
                                    defaultMessage: "Drag and drop automation"
                                })}
                            </h2>
                            <div>
                                <p css={pStyle}>
                                    {intl.formatMessage(
                                        {
                                            id: "JiraSoftwareFeaturesPageTemplate.drag-and-drop.description",
                                            defaultMessage:
                                                "Focus on the important things. Let automation do the rest. Powerful, yet simple - Jira automation is actually fun to use."
                                        },
                                        { ...DEFAULT_VALUES }
                                    )}
                                </p>
                            </div>

                            <div>
                                <Button
                                    text={intl.formatMessage({
                                        id: "JiraSoftwareFeaturesPageTemplate.drag-and-drop.learnMore",
                                        defaultMessage: "Learn more"
                                    })}
                                    url={useLocalizedHref(
                                        "/software/jira/automation-template-library"
                                    )}
                                    size={"small"}
                                    type={"primary"}
                                    color={"darkblue"}
                                    css={buttonStyles}
                                    dataEventProps={{
                                        "data-product-key":
                                            "jiraSoftwareFeature",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            "dragAndDropSection"
                                    }}
                                />
                            </div>
                            <DragAndDropImage />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DragAndDropSection;
